/* ============================================================
 * <lion-pdp-free-shipping> — Free-shipping threshold bar on PDP.
 *
 * Reads threshold (in major currency units, cents = threshold*100)
 * from data-threshold. Subscribes to cart-update so it reflects
 * cart additions in real time without a reload.
 *
 * Attributes:
 *   data-threshold      Minimum order value (e.g. "80" for €80).
 *   data-cart-total     Initial cart total in cents (server-rendered).
 *   data-before-tpl     Message before threshold met, with {amount}.
 *   data-after-tpl      Message after threshold met.
 *   data-show-bar       "true" to show progress bar (default true).
 *
 * Server pre-renders the initial state so there is no flash. JS only
 * updates it after cart changes.
 * ============================================================ */

class PdpFreeShipping extends HTMLElement {
  connectedCallback() {
    const t = parseFloat(this.dataset.threshold);
    if (!Number.isFinite(t) || t <= 0) {
      this.hidden = true;
      return;
    }
    this.thresholdCents = Math.round(t * 100);
    this.totalCents = parseInt(this.dataset.cartTotal, 10) || 0;
    this.beforeTpl = this.dataset.beforeTpl || 'Add {amount} for free shipping';
    this.afterTpl = this.dataset.afterTpl || 'You qualified for free shipping';
    this.showBar = this.dataset.showBar !== 'false';
    this.msgEl = this.querySelector('[data-pfs-message]');
    this.fillEl = this.querySelector('[data-pfs-fill]');
    this.barEl = this.querySelector('[data-pfs-bar]');

    if (!this.showBar && this.barEl) this.barEl.hidden = true;

    this.render();

    // Subscribe to global cart updates so the bar reflects new state.
    this.unsub = window.subscribe?.(window.PUB_SUB_EVENTS?.cartUpdate, () => this.refreshFromCart());
  }

  disconnectedCallback() {
    this.unsub?.();
  }

  async refreshFromCart() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      this.totalCents = cart.total_price || 0;
      this.render();
    } catch {}
  }

  render() {
    const reached = this.totalCents >= this.thresholdCents;
    const pct = Math.min(100, Math.max(0, (this.totalCents / this.thresholdCents) * 100));
    if (this.fillEl) this.fillEl.style.width = pct + '%';
    if (this.msgEl) {
      if (reached) {
        this.msgEl.textContent = this.afterTpl;
        this.classList.add('is-reached');
      } else {
        const remaining = Math.max(0, this.thresholdCents - this.totalCents);
        const amount = this.formatMoney(remaining);
        this.msgEl.textContent = this.beforeTpl.replace('{amount}', amount);
        this.classList.remove('is-reached');
      }
    }
  }

  formatMoney(cents) {
    const code = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('lion-pdp-free-shipping')) {
  customElements.define('lion-pdp-free-shipping', PdpFreeShipping);
}
